// src/components/admin/EditCohortCoaches.js - Toggle every tenant user's coach assignment for a cohort
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import './Admin.css';

const EditCohortCoaches = ({ tenantId, cohort, onBack }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [coachIds, setCoachIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);

  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, activeSearchQuery]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        status: statusFilter,
        ...(activeSearchQuery && { search: activeSearchQuery })
      });

      const usersData = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${tenantId}/users?${params}`, user),
        'Loading users...'
      );
      if (usersData.success) {
        // Only users who already hold the Coach role are eligible to be assigned
        // as a coach for this cohort - assign the role via User Management first.
        setUsers((usersData.data.users || []).filter(u => u.roles?.includes('Coach')));
      }

      const cohortData = await apiService.get(`/api/v1/tenants/${tenantId}/cohorts/${cohort.id}`, user);
      if (cohortData.success) {
        setCoachIds(new Set((cohortData.data.coaches || []).map(c => c.id)));
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const isCoach = (candidate) => coachIds.has(candidate.id);

  // Instantaneous, single-purpose call per click - deliberately not routed through
  // useApiCall so one toggle doesn't disable the whole list.
  const toggleCoach = async (targetUser) => {
    setPendingUserId(targetUser.id);
    try {
      if (isCoach(targetUser)) {
        await apiService.delete(`/api/v1/tenants/${tenantId}/cohorts/${cohort.id}/coaches/${targetUser.id}`, user);
        setCoachIds(prev => {
          const next = new Set(prev);
          next.delete(targetUser.id);
          return next;
        });
      } else {
        await apiService.post(`/api/v1/tenants/${tenantId}/cohorts/${cohort.id}/coaches`, { userId: targetUser.id }, user);
        setCoachIds(prev => new Set(prev).add(targetUser.id));
      }
    } catch (err) {
      console.error('Error updating coach assignment:', err);
    } finally {
      setPendingUserId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearchQuery(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-pill active';
      case 'invited': return 'status-pill invited';
      case 'suspended': return 'status-pill suspended';
      default: return 'status-pill archived';
    }
  };

  return (
    <div className="admin-page">
      <div className="mb-6">
        <button onClick={onBack} className="btn btn-secondary btn-sm mb-3">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to {cohort.name}
        </button>
        <h2 className="text-2xl font-semibold text-gray-800">Edit Cohort Coaches</h2>
        <p className="text-sm text-gray-600 mt-1">{cohort.name}</p>
        <p className="text-xs text-gray-500 mt-1">
          Only users with the Coach role are shown. Assign the Coach role to a user in User Management first.
        </p>
      </div>

      {/* Filters and Search */}
      <div className="filter-bar">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label mb-2">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
              style={{ minWidth: '120px' }}
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="search-group">
            <label className="form-label mb-2">Search</label>
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input flex-1"
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {activeSearchQuery && (
                <button type="button" onClick={handleClearSearch} className="btn btn-secondary" disabled={loading}>
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Loading/Error/Empty/List States */}
      {loading && users.length === 0 ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <div>
            <strong>Error loading users:</strong> {error}
          </div>
          <button onClick={loadData} className="btn btn-secondary btn-sm ml-3">
            Try Again
          </button>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state card">
          <h3>No coaches found</h3>
          <p>No one with the Coach role matches your search/filters. Assign the Coach role to someone in User Management first.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {users.map(u => {
            const coach = isCoach(u);
            const isPending = pendingUserId === u.id;

            return (
              <div key={u.id} className="user-card">
                <div className="flex flex-between items-center">
                  <div>
                    <div className="flex gap-3 items-center mb-1">
                      <span className="font-semibold text-gray-800">{u.displayName || 'No Name'}</span>
                      <span className={getStatusBadgeClass(u.status)}>{u.status}</span>
                      {coach && (
                        <span className="status-pill" style={{ backgroundColor: '#ccfbf1', color: '#0f766e' }}>
                          Coach
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{u.email}</div>
                  </div>

                  <div className="user-actions">
                    <button
                      onClick={() => toggleCoach(u)}
                      className="btn btn-sm"
                      style={coach
                        ? { backgroundColor: '#dc2626', color: 'white' }
                        : { backgroundColor: '#0f766e', color: 'white' }}
                      disabled={isPending}
                    >
                      {isPending ? 'Saving...' : coach ? 'Remove as Coach' : 'Assign as Coach'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

EditCohortCoaches.propTypes = {
  tenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cohort: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  onBack: PropTypes.func.isRequired
};

export default EditCohortCoaches;
